import {
    HeroLayoutTypeEnum,
    NavigationMenuItemAlignmentEnum,
    NavigationScrollEffectEnum,
} from "@/scripts/common/enums.js";

export default {
    data() {
        return {
            isStuck: false,
            isHidden: false,
            lastScrollY: 0,
            downScrollDelta: 0,
            upScrollDelta: 0,
        };
    },
    computed: {
        normalizedScrollEffect() {
            const raw = this.navigation?.scrollEffect;
            if (!raw) return null;
            if (raw === NavigationScrollEffectEnum.STAY_ON_TOP) return raw;
            if (raw === NavigationScrollEffectEnum.ANIMATE_OUT_AND_BACK) return raw;
            if (raw === NavigationScrollEffectEnum.SCROLL_WITH_PAGE) return raw;

            // Legacy/label fallback safety.
            const compact = raw.toString().replace(/\s+/g, "").toLowerCase();
            if (compact === "stayontop") return NavigationScrollEffectEnum.STAY_ON_TOP;
            if (compact === "animateout&back" || compact === "animateoutandback") return NavigationScrollEffectEnum.ANIMATE_OUT_AND_BACK;
            if (compact === "scrollwithpage") return NavigationScrollEffectEnum.SCROLL_WITH_PAGE;
            return null;
        },
        isStickyEffect() {
            return this.normalizedScrollEffect === NavigationScrollEffectEnum.STAY_ON_TOP;
        },
        isAutoHideEffect() {
            return this.normalizedScrollEffect === NavigationScrollEffectEnum.ANIMATE_OUT_AND_BACK;
        },
        isPinnedEffect() {
            return this.isStickyEffect || this.isAutoHideEffect;
        },
        isSidebarHeroNavigation() {
            const portfolio = this.viewData?.portfolio || this.$store.state.portfolio;
            return portfolio?.portfolioHero?.layoutSettings?.layoutType === HeroLayoutTypeEnum.SIDEBAR;
        },
        menuItemAlignmentClass() {
            if (!this.isSidebarHeroNavigation) return null;
            return this.navigation?.layout?.menuItemAlignment === NavigationMenuItemAlignmentEnum.HORIZONTAL
                ? "menu-items-horizontal"
                : "menu-items-vertical";
        },
        hasAnimatedHeroBackground() {
            const portfolio = this.viewData?.portfolio || this.$store.state.portfolio;
            return !!portfolio?.portfolioHero?.backgroundSettings?.animatedBackground;
        },
        navbarClasses() {
            return [
                "navbar",
                this.layoutClass,
                this.styleClass,
                this.frameLayoutClass,
                this.menuItemAlignmentClass,
                `pn-${this.theme || ""}`,
                {
                    sticky: this.isPinnedEffect,
                    stuck: this.isPinnedEffect && this.isStuck,
                    "auto-hide": this.isAutoHideEffect,
                    "is-hidden": this.isHidden,
                    "on-animated-hero": this.hasAnimatedHeroBackground,
                    "menu-items-wrapped": this.navItemsWrapped,
                },
            ];
        },
    },
    methods: {
        hasVisibleProcessStickyHeader() {
            if (typeof document === "undefined") return false;
            return !!document.querySelector(".ufo-sec--process .sticky-header.sticked:not(.sticked-hidden)");
        },
        clearPinnedNavOffset() {
            const root = document?.documentElement;
            if (!root) return;
            root.style.setProperty("--portfolioPinnedNavOffset", "0px");
        },
        applyPinnedNavOffset() {
            const root = document?.documentElement;
            if (!root) return;

            let offset = 0;
            const navVisible = this.isPinnedEffect && this.isStuck && !(this.isAutoHideEffect && this.isHidden);
            if (navVisible) {
                offset = Math.round(this.$el?.offsetHeight || 0);
            }

            root.style.setProperty("--portfolioPinnedNavOffset", `${offset}px`);
        },
        getWindowScrollY() {
            return typeof window === "undefined" ? 0 : (window.scrollY || 0);
        },
        resetScrollState() {
            this.isStuck = false;
            this.isHidden = false;
            this.downScrollDelta = 0;
            this.upScrollDelta = 0;
            this.lastScrollY = this.getWindowScrollY();
        },
        setupScrollListener() {
            this.teardownScrollListener();
            if (!this.isPinnedEffect || typeof window === "undefined") {
                this.resetScrollState();
                this.clearPinnedNavOffset();
                return;
            }
            this.resetScrollState();
            window.addEventListener("scroll", this.handleScroll, { passive: true });
            window.addEventListener("resize", this.applyPinnedNavOffset, { passive: true });
            this.handleScroll();
        },
        teardownScrollListener() {
            if (typeof window !== "undefined") {
                window.removeEventListener("scroll", this.handleScroll);
                window.removeEventListener("resize", this.applyPinnedNavOffset);
            }
            this.clearPinnedNavOffset();
        },
        handleScroll() {
            const currentY = Math.max(this.getWindowScrollY(), 0);
            const hiddenByProcess = this.hasVisibleProcessStickyHeader();

            if (!this.isAutoHideEffect) {
                this.isStuck = currentY > 0;
                this.isHidden = hiddenByProcess;
                this.lastScrollY = currentY;
                this.applyPinnedNavOffset();
                return;
            }

            // Keep nav visible near the top.
            if (currentY < 80) {
                this.isStuck = false;
                this.isHidden = false;
                this.downScrollDelta = 0;
                this.upScrollDelta = 0;
                this.lastScrollY = currentY;
                this.applyPinnedNavOffset();
                return;
            }

            this.isStuck = true;
            if (hiddenByProcess) {
                this.isHidden = true;
                this.downScrollDelta = 0;
                this.upScrollDelta = 0;
                this.lastScrollY = currentY;
                this.applyPinnedNavOffset();
                return;
            }
            const delta = currentY - this.lastScrollY;

            if (delta > 0) {
                this.downScrollDelta += delta;
                this.upScrollDelta = 0;
                if (this.downScrollDelta >= 60) {
                    this.isHidden = true;
                    this.downScrollDelta = 0;
                }
            } else if (delta < 0) {
                this.upScrollDelta += Math.abs(delta);
                this.downScrollDelta = 0;
                if (this.upScrollDelta >= 24) {
                    this.isHidden = false;
                    this.upScrollDelta = 0;
                }
            }

            this.lastScrollY = currentY;
            this.applyPinnedNavOffset();
        },
    },
    mounted() {
        this.setupScrollListener();
    },
    beforeUnmount() {
        this.teardownScrollListener();
    },
    watch: {
        isPinnedEffect() {
            this.setupScrollListener();
        },
    },
};
